<template>
  <div class="col">
    <span class="d-block text-gray-600 mb-1">{{ title }}</span>
    <div class="row flex-column gy-1 gy-md-2">
      <div v-for="product in products" :key="product.id" class="col-12">
        <div class="per-card" :class="{ disabled: !canPersonalize(product) }">
          <div class="col-auto mx-0">
            <input
              type="checkbox"
              class="form-check-input"
              :id="`${prefix}-${product.id}`"
              :checked="isProductSelected(product.id)"
              :disabled="!canPersonalize(product)"
              @change="toggleProductSelection(product.id)"
            />
          </div>

          <div
            class="col-auto ps-0 pe-1 ps-lg-05 pe-lg-2"
            @click="toggleProductSelection(product.id)"
          >
            <figure class="per-card__img ms-1">
              <img :src="product.image" :alt="product.name" />
            </figure>
          </div>

          <div class="col">
            <span class="per-card__title lh-sm">
              <span v-if="product.count > 1" class="per-card__quantity"
                >{{ product.count }}x
              </span>
              {{ product.name }}
            </span>
          </div>

          <div class="col-12 col-lg-auto">
            <div
              v-if="canPersonalize(product)"
              class="row align-items-center g-1"
            >
              <div
                v-for="optionType in personalizationOptions"
                :key="optionType.type"
                class="col-auto"
              >
                <div
                  :class="[
                    'per-card__option',
                    {
                      'per-card__option--active':
                        personalization[product.id]?.[optionType.type],
                      'per-card__option--disabled': !canPersonalizeOption(
                        product,
                        optionType.type,
                      ),
                    },
                  ]"
                  data-bs-toggle="tooltip"
                  data-bs-placement="top"
                  :data-bs-title="optionType.tooltip"
                  data-bs-trigger="hover"
                  data-bs-delay="0"
                  @click="
                    togglePersonalizationOption(product.id, optionType.type)
                  "
                >
                  {{ optionType.label }}
                  <i class="icon icon-question"></i>
                </div>
              </div>
            </div>
          </div>

          <div v-if="!canPersonalize(product)" class="col-auto">
            <span class="per-card__option per-card__option--disabled">
              Product niet te personaliseren
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Tooltip } from 'bootstrap';

export default {
  name: 'PerCardGroup',
  props: {
    title: {
      type: String,
      required: true,
    },
    products: {
      type: Array,
      required: true,
    },
    prefix: {
      type: String,
      required: true,
    },
    personalization: {
      type: Object,
      required: true,
    },
  },
  emits: ['update'],
  data() {
    return {
      personalizationOptions: [
        {
          type: 'sticker',
          label: 'Sticker',
          tooltip: 'Zelfklevende print voor opvallende personalisatie.',
        },
        {
          type: 'zeefdruk',
          label: 'Zeefdruk',
          tooltip:
            'Inkt rechtstreeks op het product gedrukt, duurzaam resultaat.',
        },
        {
          type: 'graveren',
          label: 'Graveren',
          tooltip: 'Tekst of logo permanent in het product ingesneden.',
        },
      ],
    };
  },
  watch: {
    products: {
      handler() {
        this.$nextTick(() => {
          this.initializeTooltips();
        });
      },
      deep: true,
    },
  },
  mounted() {
    this.initializeTooltips();
  },
  methods: {
    initializeTooltips() {
      document
        .querySelectorAll('[data-bs-toggle="tooltip"]')
        .forEach((el) => new Tooltip(el, { trigger: 'hover' }));
    },
    reinitializeTooltips() {
      this.$nextTick(this.initializeTooltips);
    },
    hideAllTooltips() {
      const tooltipElements = document.querySelectorAll(
        '[data-bs-toggle="tooltip"]',
      );
      tooltipElements.forEach((element) => {
        const tooltip = Tooltip.getInstance(element);
        if (tooltip) {
          tooltip.hide();
        }
      });

      document.querySelectorAll('.tooltip').forEach((tooltip) => {
        tooltip.remove();
      });

      document.querySelectorAll('.tooltip-arrow').forEach((arrow) => {
        arrow.remove();
      });
    },
    canPersonalize(product) {
      if (!product || !product.count || product.count <= 0) {
        return false;
      }

      return this.personalizationOptions.some((option) =>
        this.canPersonalizeOption(product, option.type),
      );
    },

    canPersonalizeOption(product, optionType) {
      if (!product || !product.attributes) {
        return false;
      }

      const attributeName = `pa_${optionType}`;
      const attribute = product.attributes.find(
        (attr) => attr.name === attributeName,
      );
      if (!attribute) {
        return false;
      }

      let value = '';
      if (attribute.value) {
        value = attribute.value;
      } else if (attribute.options && attribute.options.length > 0) {
        value = attribute.options[0];
      } else if (attribute.option) {
        value = attribute.option;
      } else if (attribute.name) {
        value = attribute.name;
      }

      if (!value || value.trim() === '') {
        return false;
      }

      const stringValue = value.toString().toLowerCase().trim();
      return (
        stringValue === 'ja' ||
        stringValue === 'yes' ||
        stringValue === 'true' ||
        stringValue === '1'
      );
    },

    isProductSelected(productId) {
      const personalization = this.personalization[productId];
      if (!personalization) {
        return false;
      }

      return (
        personalization.sticker ||
        personalization.zeefdruk ||
        personalization.graveren
      );
    },

    toggleProductSelection(productId) {
      if (!this.personalization[productId]) {
        this.personalization[productId] = {
          sticker: false,
          zeefdruk: false,
          graveren: false,
        };
      }

      const product = this.products.find((p) => p.id === productId);
      if (!product) {
        return;
      }

      const isCurrentlySelected = this.isProductSelected(productId);

      const updatedPersonalization = { ...this.personalization };
      updatedPersonalization[productId] = {
        ...updatedPersonalization[productId],
      };

      if (isCurrentlySelected) {
        updatedPersonalization[productId] = {
          sticker: false,
          zeefdruk: false,
          graveren: false,
        };
      } else {
        const firstAvailableOption = ['sticker', 'zeefdruk', 'graveren'].find(
          (option) => this.canPersonalizeOption(product, option),
        );

        if (firstAvailableOption) {
          updatedPersonalization[productId] = {
            sticker: false,
            zeefdruk: false,
            graveren: false,
          };
          updatedPersonalization[productId][firstAvailableOption] = true;
        }
      }

      // Use requestAnimationFrame to batch updates for better performance
      this.$nextTick(() => {
        this.$emit('update', updatedPersonalization);
        // Defer tooltip hiding to avoid blocking the main thread
        requestAnimationFrame(() => {
          this.hideAllTooltips();
        });
      });
    },

    togglePersonalizationOption(productId, optionType) {
      if (!this.personalization[productId]) {
        this.personalization[productId] = {
          sticker: false,
          zeefdruk: false,
          graveren: false,
        };
      }

      const product = this.products.find((p) => p.id === productId);
      if (!product) {
        return;
      }

      if (this.canPersonalizeOption(product, optionType)) {
        const updatedPersonalization = { ...this.personalization };
        updatedPersonalization[productId] = {
          ...updatedPersonalization[productId],
        };

        const isCurrentlyActive = updatedPersonalization[productId][optionType];

        if (isCurrentlyActive) {
          updatedPersonalization[productId][optionType] = false;
        } else {
          updatedPersonalization[productId] = {
            sticker: false,
            zeefdruk: false,
            graveren: false,
          };
          updatedPersonalization[productId][optionType] = true;
        }

        // Use requestAnimationFrame to batch updates for better performance
        this.$nextTick(() => {
          this.$emit('update', updatedPersonalization);
          // Defer tooltip hiding to avoid blocking the main thread
          requestAnimationFrame(() => {
            this.hideAllTooltips();
          });
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped></style>
